import Class from '@core/Class';
import Module from '@core/Module';
import View from '@core/View';
import Model from '@core/Model';
import assign from 'object-assign';

/* public methods */
function update_slides () {
    var slides = this.slide_collection.toJSON(),
        viewData = {
            slides: slides,
            controller_type: this.controller_type + '-controller'
        };
    // :controller_type is used to delegate events for multiple controllers that might be used in a photoGallery module
    // to avoid event conflicts from occuring
    // reset slider
    if ( this.controls.remove ) {
        this.controls.remove();
    }
    // set thumb-list to first item
    this.current_index = 0;

    var tmpl = this.view.render( viewData );
    // update view
    this.el.prepend( tmpl );
    this.update_elements();
    // highlight active control upon re-render
    this.trigger( 'rendered-slider:slide' );
}

function handle_slides_change ( e ) {
    var slide_id = e.currentTarget.getAttribute( 'data-slideid' );
    if ( slide_id ) {
        this.change_slide( slide_id );
    }
}

function change_slide ( slide_id ) {
    // set current_index of selected target from control_list
    this.current_index = this.slide_collection.getIndex( slide_id );
    this.trigger( 'change:slide', this.slide_collection.find( slide_id ).toJSON() );
}

function toggle_current_control () {
    this.control_list_items.removeClass( 'on' );
    this.control_list_items.eq( this.current_index ).addClass( 'on' );
}

function next ( e ) {
    this.set_page_index( this.page_index + 1 );
}

function previous ( e ) {
    this.set_page_index( this.page_index - 1 );
}

function apply_pagination () {
    var inc = this.slide_increment,
        list = this.control_list,
        items = this.control_list_items,
        paging_multiplier = Math.ceil( items.length / inc );

    // calculate each slides width by finding max-length of slides
    // use percentages for adaptive layout
    items.width( (100 / (inc * paging_multiplier)) + '%' );

    // set inner width of slide list container to control swipe animation: total number slides * 100
    list.width( 100 * paging_multiplier + '%' );
    // set max paging index calcaulated total number slides / slides per view
    this.max_page_index = paging_multiplier - 1;
}

function set_page_index ( val ) {
    if ( val <= 0 ) {
        this.page_index = 0;
    }
    else if ( val >= this.max_page_index ) {
        this.page_index = this.max_page_index;
    }
    else {
        this.page_index = val;
    }
    this.trigger( 'paginate:slide' );
}

/*
 * Set scroll pos of view pane to the offset of specified index
 * this method work from a range base on current scroll posiiton and pane height
 * and sets the offset +/- to the item.offsetHeight to bring just into view
 */
function scroll_to_index ( index ) {
    var thumb_item = this.control_list_items[index],
        scroll_pane = this.controls.parent(),
        ctx = scroll_pane[0],
        // default to current scroll position
        new_pos = (ctx.scrollTop >= 0) ? ctx.scrollTop : 0,
        // height of thumb-list view pane
        h = ctx.offsetHeight,
        // where current item lies in context of view pane
        offset = thumb_item.offsetTop - ctx.offsetTop;

    if ( offset < ctx.scrollTop ) {
        new_pos = offset;
    }
    else if ( offset >= h + ctx.scrollTop ) {
        new_pos = (offset - h) + thumb_item.offsetHeight;
    }

    scroll_pane.stop().animate( {scrollTop: new_pos}, 300 );
}

function toggle_forward_back_controls () {
    this.control_previous.removeClass( 'disabled' );
    this.control_next.removeClass( 'disabled' );

    if ( this.page_index == 0 ) {
        this.control_previous.addClass( 'disabled' );
    }
    if ( this.page_index == this.max_page_index ) {
        this.control_next.addClass( 'disabled' );
    }
}

export default {
    create: function ( config ) {

        var defaults = assign( {
            attributes: [],
            remote_query: null,
            current_index: 0,
            page_index: 0,
            preserve_paging: false,
            slide_increment: 7,
            type: 'thumblist' // thumblist, slider
        }, config || {} );

        /* Common initialize to be used by all factory instances */
        // @param collection {Object||Function=>Object} - collection can be an object representing the model of a function that parses
        // the existing DOM to generate and return an object to be passed as a model collection or left undefined
        // and defining a remote resource that returns JSON that will be passed to the model
        function initialize ( el ) {
            this.controller_type = defaults.type;

            // generate slide model: default attributes require [id, src]
            this.slide_collection.remote_query = defaults.remote_query;

            this.slide_collection.attributes = $.merge( ['src'], defaults.attributes );

            // populate the slide collection and render the thumbnail controls
            this.slide_collection.bind( 'populated:model', this.proxy( function ( e ) {
                this.update_slides();
                this.trigger( 'change:slide',
                    this.slide_collection.findByIndex( defaults.current_index ).toJSON() );
            } ) );

            this.slide_collection.bind( 'error:model', this.proxy( function () {
                this.trigger( 'error:slide', {msg: 'Failed to load resource for Photo Gallery'} );
            } ) );

            // render slide controller once model is populated with new data
            this.bind( 'change:slide rendered-slider:slide', this.proxy( this.toggle_current_control ) );

            // initialize DOM
            this.ready( function () {
                this.el.addClass( 'enabled' );

                // populate model from remote resource
                if ( defaults.remote_query ) {
                    this.slide_collection.load_remote()
                }
                // if not loading remotely, collection should be set manually after module init from object instance
            } );
        }

        var thumblist = {
            elements: {
                '.thumblist-controller': 'controls',
                '.upi-gallery-thumb-list': 'control_list',
                '.upi-gallery-thumb-list li': 'control_list_items'
            },
            events: {
                'click .upi-gallery-thumb-list li': 'handle_slides_change'
            },
            current_index: defaults.current_index,
            slide_collection: Model.extend(),
            update_slides: update_slides,
            handle_slides_change: handle_slides_change,
            change_slide: change_slide,
            scroll_to_index: scroll_to_index,
            toggle_current_control: toggle_current_control,

            init: function ( el, collection ) {
                // initialize view tempaltes first
                this.view = new View( 'galleryThumbController', this );

                initialize.apply( this, arguments );

                this.ready( function () {
                    this.el.addClass( 'upi-gallery-list' );
                    this.bind( 'change:slide', this.proxy( function ( e ) {
                        this.scroll_to_index( this.current_index );
                    } ) );
                } );
                // return module object
                return this;
            }
        };

        var slider = {
            elements: {
                '.slider-controller': 'controls',
                '.slider-controller .prev': 'control_previous',
                '.slider-controller .next': 'control_next',
                '.upi-gallery-slider-list': 'control_list',
                '.upi-gallery-slider-list li': 'control_list_items'
            },
            events: {
                'click .upi-gallery-slider-list li': 'handle_slides_change',
                'click control_previous': 'previous',
                'click control_next': 'next'
            },
            slide_increment: defaults.slide_increment,
            page_index: defaults.page_index,
            current_index: defaults.current_index,
            slide_collection: Model.extend(),
            update_slides: update_slides,
            handle_slides_change: handle_slides_change,
            change_slide: change_slide,
            toggle_current_control: toggle_current_control,
            apply_pagination: apply_pagination,
            next: next,
            previous: previous,
            set_page_index: set_page_index,
            toggle_forward_back_controls: toggle_forward_back_controls,

            init: function ( el, collection ) {
                // initialize view templates first
                this.view = new View( 'gallerySlideController', this );
                initialize.apply( this, arguments );

                this.bind( 'paginate:slide', this.proxy( function () {
                    this.control_list.animate( {marginLeft: -(100 * this.page_index) + '%'} );
                    this.toggle_forward_back_controls();
                } ) );

                // slider initialized
                this.bind( 'rendered-slider:slide', this.proxy( function () {
                    this.apply_pagination();
                    this.toggle_forward_back_controls();
                } ) );

                // initialize DOM
                this.ready( function () {
                    // determine if pagination should be toggled anytime a slide changes
                    this.bind( 'change:slide', this.proxy( function () {
                        var inc = defaults.slide_increment,
                            max = (this.page_index + 1) * inc,
                            min = max - inc;

                        if ( this.current_index <= min || this.current_index >= max ) {
                            // sets page_index to a decimal which sets page start @ current index
                            // parseInt() sets page_index to integer which keeps paging rounded
                            var to_index = (defaults.preserve_paging) ?
                                parseInt( this.current_index / inc ) :
                                this.current_index /
                                inc;
                            this.set_page_index( to_index );
                        }
                    } ) );

                    this.el.addClass( 'upi-gallery-slider' );
                } );

                // return module object
                return this;
            }
        };

        /* Factory: return a Class instance of ThumbnailController */
        // refactor: these probably warant separate factory functions (as import) instead of relying on a config toggle
        const ThumbnailFactory = Class
            .extend( Module )
            .extend( ('slider' === defaults.type) ? slider : thumblist );
        return ThumbnailFactory;
    }
};
